import { JobTitle } from './JobTitle';
import { JobDescription } from './JobDescription';
import { JobCardReview } from './JobCardReview';
import { When } from 'react-if';
import { JobMedia } from './Media/JobMedia';
import styled, { keyframes } from 'styled-components6';
import { PluginWorkSite } from 'rwl-shared/dist/models';
import { PluginReview } from 'rwl-shared/dist/models/plugin/Plugin';
import { pluginMediaFromJob } from '../../../utils/pluginMediaFromJob';
import { findReview } from '../../../utils/findReview';
import { mqDown, mqUp } from '../../../theme';
import { ReviewRating } from '../../ReviewRating';
import { ForwardedRef, forwardRef, useImperativeHandle, useRef } from 'react';
import { jobTitleGenerator } from 'rwl-shared/dist/utils/jobs/jobTitleGenerator';
import { ShowcaseChip } from '../../Showcase';
import { LabelList } from './LabelsList';

const JOB_CARD_TRANSITION_LENGTH = '0.5s';

const StyledJobCard = styled.div`
    display: flex;
    flex-direction: row;
    height: var(--rwl-jobCardMediaHeight-lg);
    outline: 1px solid var(--rwl-palette-gray-300);
    border-radius: var(--rwl-spacing-sm);
    cursor: pointer;
    ${mqDown('lg')} {
        height: var(--rwl-jobCardMediaHeight-sm);
    }
    ${mqDown('xs')} {
        height: var(--rwl-jobCardMediaHeight-xs);
    }

    &.smallMode {
        // bit of a hack, really we should move to container queries
        --rwl-jobCardMediaHeight-xs: 156px;
        --rwl-jobCardMediaHeight-sm: 156px;
        --rwl-jobCardMediaHeight-md: 156px;
        --rwl-jobCardMediaHeight-lg: 156px;
        --rwl-jobCardMediaWidth-xs: 156px;
        --rwl-jobCardMediaWidth-sm: 156px;
        --rwl-jobCardMediaWidth-md: 156px;
        --rwl-jobCardMediaWidth-lg: 156px;
    }

    &:hover {
        outline: 1px solid var(--rwl-palette-gray-500);
        box-shadow: 0px 0px 8px 0px var(--rwl-palette-gray-200);
    }
    transition: box-shadow 0.2s ease-in-out, outline 0.2s ease-in-out;
    position: relative;
`;

const JobCardContent = styled.div`
    display: flex;
    flex-direction: column;
    padding: var(--rwl-spacing-md);
    width: calc(100% - var(--rwl-jobCardMediaWidth-lg));

    // Keep these in sync with MediaContainer in ./Media/JobMedia.tsx
    ${mqUp('xl')} {
        width: calc(100% - var(--rwl-jobCardMediaWidth-lg));
    }
    ${mqDown('xl')} {
        width: calc(100% - var(--rwl-jobCardMediaWidth-md));
    }
    ${mqDown('lg')} {
        width: calc(100% - var(--rwl-jobCardMediaWidth-sm));
    }
    ${mqDown('xs')} {
        width: calc(100% - var(--rwl-jobCardMediaWidth-xs));
    }
`;

const SmallScreenStars = styled.div`
    display: none;
    ${mqDown('xs')} {
        display: flex;
        justify-content: flex-start;
        margin-top: var(--rwl-spacing-sm);
        align-items: center;
    }

    &.smallMode {
        display: flex;
        justify-content: flex-start;
        margin-top: var(--rwl-spacing-sm);
    }
`;

type JobCardProps = {
    job: PluginWorkSite;
    onJobCardClicked: () => void;
    smallMode?: boolean;
    similarJobClass?: string;
};

export const JobCard = forwardRef(
    (
        {
            job,
            onJobCardClicked,
            smallMode = false,
            similarJobClass,
        }: JobCardProps,
        ref: ForwardedRef<HTMLElement>
    ): JSX.Element => {
        const jobReviews: PluginReview[] = [];
        job.content.map((content) => {
            if (content.type === 'Review') {
                jobReviews.push(content);
            }
        });

        const divRef = useRef<HTMLDivElement>(null);
        useImperativeHandle(ref, () => divRef.current as HTMLElement);

        const classes = ['jobCard'];
        if (smallMode) {
            classes.push('smallMode');
        }
        if (similarJobClass) {
            classes.push(similarJobClass);
        }

        return (
            <StyledJobCard
                onClick={onJobCardClicked}
                data-testid="job-card"
                className={classes.join(' ')}
                id={'job-' + job.id}
                ref={divRef}
            >
                <When condition={job.showcase}>
                    <ShowcaseChip />
                </When>
                <JobMedia
                    media={pluginMediaFromJob(job)}
                    staticMapUrl={job?.staticMapUrl}
                />
                <JobCardContent>
                    <JobTitle title={jobTitleGenerator(job)} />
                    <When condition={!!job.description && !jobReviews?.length}>
                        <JobDescription description={job.description} />
                    </When>
                    <When condition={!!jobReviews?.length}>
                        <JobCardReview review={findReview(job)} />
                    </When>
                    <When condition={!smallMode}>
                        <LabelList labels={job.labels} />
                    </When>
                    <SmallScreenStars>
                        <ReviewRating
                            size={'small'}
                            rating={jobReviews[0]?.rating}
                            source={jobReviews[0]?.source}
                        />
                    </SmallScreenStars>
                </JobCardContent>
            </StyledJobCard>
        );
    }
);

const scrollingGradient = keyframes` 
    0%   { background-position: 0 0; }
    100% { background-position: -200% 0; }
`;

export const JobCardLoader = styled(StyledJobCard)<{ $visible: string }>`
    opacity: ${({ $visible }) => ($visible === 'true' ? 1 : 0)};
    transition: opacity ${JOB_CARD_TRANSITION_LENGTH} ease-in-out;
    pointer-events: none;
    cursor: default;
    background: repeating-linear-gradient(
        to right,
        var(--rwl-palette-gray-100) 0%,
        var(--rwl-palette-gray-100) 25%,
        var(--rwl-palette-gray-300) 50%,
        var(--rwl-palette-gray-100) 75%,
        var(--rwl-palette-gray-100) 100%
    );
    width: 100%;
    height: var(--rwl-spacing-xl);
    outline: none;
    background-size: 200% auto;
    background-position: 0 100%;
    animation: ${scrollingGradient} 5s infinite;
    animation-fill-mode: forwards;
    animation-timing-function: linear;
`;
